import { pgTable, timestamp, integer, index, foreignKey, boolean, text, uniqueIndex } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { subscriptionStatus, familyMemberStatus, paymentProvider } from './enums';
import { user } from './user';
import { subscriptionPlan } from './subscription-plan';
import { pricingTier } from './pricing-tier';

/**
 * Subscription - Подписка пользователя
 * Расширена для поддержки семейных абонементов
 */
export const subscription = pgTable('Subscription', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(), // Владелец подписки

  // Связь с планом (новое)
  subscriptionPlanId: integer(), // FK → SubscriptionPlan

  // Конкретный тариф, по которому оформлена подписка.
  // Источник истины для названия/картинки тарифа (resolveTierData).
  // Сохраняется при покупке, ручной выдаче и приглашении — заполнен всегда,
  // когда тариф был выбран. null только у совсем старых legacy-подписок.
  pricingTierId: integer(), // FK → PricingTier

  // Legacy поле для обратной совместимости
  productId: integer(), // reference to Product type=SUBSCRIPTION

  status: subscriptionStatus().default('PENDING').notNull(),
  startDate: timestamp({ precision: 3, mode: 'date' }),
  endDate: timestamp({ precision: 3, mode: 'date' }),

  // Семейная подписка
  isFamilyOwner: boolean().default(false).notNull(),
  currentFamilySlots: integer().default(0).notNull(), // Максимальное кол-во семейных слотов (из тарифа)

  sourceOrderId: integer(),
  sourcePaymentId: integer(),
  cancelledAt: timestamp({ precision: 3, mode: 'date' }),
  // Аналитика когорт: 'YYYY-MM' первого подтверждённого платежа пользователя.
  // Вычисляется AnalyticsDenormService при первом confirm. Используется в
  // cohort-retention отчётах. Nullable у legacy-подписок без платежей.
  cohortMonth: text(),
  // Счётчик продлений (renewal). 0 при первой покупке, +1 при каждом продлении.
  renewalCount: integer().default(0).notNull(),
  // Автопродление: пользователь может отключить в профиле. true по умолчанию.
  // Когда false — renewal-worker'ы пропускают подписку (доступ доживает до endDate,
  // дальше подписку подберёт subscription-expiry.worker и переведёт в EXPIRED).
  autoRenew: boolean().default(true).notNull(),
  // Каким эквайрингом продлевать (источник токена карты для MIT-списания).
  // Проставляется при первом успешном платеже по подписке = провайдер оплаты.
  // null у legacy-подписок без сохранённой карты — такие не продлеваются.
  // CLOUDPAYMENTS → subscription-renewal.worker; MULTICARD → multicard-subscription-renewal.worker.
  recurringProvider: paymentProvider(),
  // Причина отмены подписки (UI-комментарий или код). Nullable.
  cancellationReason: text(),
  // Когда пользователю была показана welcome-онбординг модалка для этой подписки.
  // null = модалка ещё не показывалась, нужно показать при следующем заходе в mini-app.
  welcomeShownAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  index('Subscription_endDate_idx').using('btree', table.endDate.asc().nullsLast()),
  index('Subscription_productId_idx').using('btree', table.productId.asc().nullsLast()),
  index('Subscription_subscriptionPlanId_idx').using('btree', table.subscriptionPlanId.asc().nullsLast()),
  index('Subscription_pricingTierId_idx').using('btree', table.pricingTierId.asc().nullsLast()),
  index('Subscription_status_idx').using('btree', table.status.asc().nullsLast()),
  index('Subscription_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('Subscription_isFamilyOwner_idx').using('btree', table.isFamilyOwner.asc().nullsLast()),
  index('Subscription_cohortMonth_idx').using('btree', table.cohortMonth.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'Subscription_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.subscriptionPlanId],
    foreignColumns: [subscriptionPlan.id],
    name: 'Subscription_subscriptionPlanId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.pricingTierId],
    foreignColumns: [pricingTier.id],
    name: 'Subscription_pricingTierId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

/**
 * SubscriptionMember - Член семейной подписки
 * Хранит связь пользователей с семейной подпиской
 */
export const subscriptionMember = pgTable('SubscriptionMember', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  subscriptionId: integer().notNull(),
  userId: integer().notNull(),
  status: familyMemberStatus().default('ACTIVE').notNull(),
  addedAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  removedAt: timestamp({ precision: 3, mode: 'date' }),
  removedReason: text(),
}, (table) => [
  index('SubscriptionMember_subscriptionId_idx').using('btree', table.subscriptionId.asc().nullsLast()),
  index('SubscriptionMember_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('SubscriptionMember_status_idx').using('btree', table.status.asc().nullsLast()),
  uniqueIndex('SubscriptionMember_subscriptionId_userId_key').using('btree', table.subscriptionId, table.userId),
  foreignKey({
    columns: [table.subscriptionId],
    foreignColumns: [subscription.id],
    name: 'SubscriptionMember_subscriptionId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'SubscriptionMember_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);
